import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, API, errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { MeetingCard } from '../components/MeetingCard';
import { toast } from 'sonner';
import { Paperclip, Smiley, LinkSimple, PaperPlaneRight, UsersThree, Plus, Checks, Check, FileText, CalendarPlus, ArrowsLeftRight, PencilSimple, Archive } from '@phosphor-icons/react';

const EMOJIS = ['😀', '😄', '😂', '🙂', '😉', '😍', '🤔', '👍', '👏', '🙏', '💯', '🎉', '✅', '❌', '⚠️', '📌', '📚', '🏫', '🧪', '⚽', '🎨', '💰', '📅', '❤️'];
const STAFF = ['global_admin', 'school_admin', 'principal', 'registrar', 'admissions_officer', 'teacher', 'finance_admin'];
const GC_CREATORS = ['global_admin', 'school_admin', 'principal', 'registrar', 'teacher'];
const MODULE_LINKS = [['Students', '/students'], ['Admissions', '/admissions'], ['Academics', '/academics'], ['Attendance', '/attendance'], ['Fees', '/fees'], ['Operations', '/operations'], ['Lifecycle', '/lifecycle'], ['IAM & IGA', '/iam']];
const INTERNAL_ROUTES = MODULE_LINKS.map(([, p]) => p);

const renderText = (text) => {
  if (!text) return null;
  return text.split(/(\s+)/).map((tok, i) => {
    if (/^https?:\/\//.test(tok)) return <a key={`${i}-${tok}`} href={tok} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">{tok}</a>;
    if (INTERNAL_ROUTES.includes(tok)) return <Link key={`${i}-${tok}`} to={tok} className="text-blue-600 underline font-medium">{tok}</Link>;
    return tok;
  });
};

export default function Chat() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [active, setActive] = useState(null);
  const [text, setText] = useState('');
  const [pending, setPending] = useState([]);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selected, setSelected] = useState([]);
  const [meetOpen, setMeetOpen] = useState(false);
  const [meetForm, setMeetForm] = useState({ purpose: '', slots: ['', '', ''] });
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferTo, setTransferTo] = useState('');
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const isStaff = STAFF.includes(user?.role);
  const canGuardianThread = GC_CREATORS.includes(user?.role);
  const [gtStudent, setGtStudent] = useState('');

  const { data: convs } = useQuery({ queryKey: ['chat-convs'], queryFn: () => api.get('/chat/conversations').then((r) => r.data), refetchInterval: 5000 });
  const { data: contacts } = useQuery({ queryKey: ['chat-contacts'], queryFn: () => api.get('/chat/contacts').then((r) => r.data), enabled: isStaff });
  const { data: students } = useQuery({ queryKey: ['chat-students'], queryFn: () => api.get('/students').then((r) => r.data), enabled: canGuardianThread });
  const { data: messages } = useQuery({
    queryKey: ['chat-msgs', active],
    queryFn: () => api.get(`/chat/conversations/${active}/messages`).then((r) => r.data),
    enabled: !!active, refetchInterval: 3000,
  });
  const { data: meetings } = useQuery({
    queryKey: ['chat-meetings', active],
    queryFn: () => api.get(`/chat/conversations/${active}/meetings`).then((r) => r.data),
    enabled: !!active, refetchInterval: 5000,
  });
  const activeConv = (convs || []).find((c) => c.id === active);
  const { data: plugins } = useQuery({
    queryKey: ['meeting-plugins-chat', activeConv?.tenant_id],
    queryFn: () => api.get('/meetings/plugins', { params: { tenant_id: activeConv?.tenant_id } }).then((r) => r.data),
    enabled: !!activeConv?.tenant_id,
  });
  const meetingById = Object.fromEntries((meetings || []).map((m) => [m.id, m]));

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages?.length]);

  const invalMsgs = () => { qc.invalidateQueries({ queryKey: ['chat-msgs', active] }); qc.invalidateQueries({ queryKey: ['chat-convs'] }); qc.invalidateQueries({ queryKey: ['chat-meetings', active] }); };

  const convMut = useMutation({
    mutationFn: (body) => api.post('/chat/conversations', body),
    onSuccess: (r) => { setActive(r.data.id); setNewOpen(false); setSelected([]); setGroupName(''); qc.invalidateQueries({ queryKey: ['chat-convs'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const gtMut = useMutation({
    mutationFn: () => api.post('/chat/guardian-threads', { student_id: gtStudent }),
    onSuccess: (r) => { setActive(r.data.id); setNewOpen(false); setGtStudent(''); toast.success('Guardian thread ready'); qc.invalidateQueries({ queryKey: ['chat-convs'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const modMut = useMutation({
    mutationFn: (mid) => api.delete(`/chat/messages/${mid}`),
    onSuccess: () => { toast.success('Message removed'); qc.invalidateQueries({ queryKey: ['chat-msgs', active] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const sendMut = useMutation({
    mutationFn: () => api.post(`/chat/conversations/${active}/messages`, { text, attachments: pending }),
    onSuccess: () => { setText(''); setPending([]); invalMsgs(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const stmtMut = useMutation({
    mutationFn: () => api.post(`/chat/conversations/${active}/share-statement`),
    onSuccess: () => { toast.success('Fee statement dropped into the thread'); invalMsgs(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const meetReqMut = useMutation({
    mutationFn: () => api.post(`/chat/conversations/${active}/meeting-requests`, { purpose: meetForm.purpose, slots: meetForm.slots.filter(Boolean) }),
    onSuccess: () => { toast.success('Meeting request sent'); setMeetOpen(false); setMeetForm({ purpose: '', slots: ['', '', ''] }); invalMsgs(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const acceptMut = useMutation({
    mutationFn: ({ id, slot, provider }) => api.post(`/meetings/${id}/accept`, { slot, provider }),
    onSuccess: () => { toast.success('Meeting confirmed - join link posted in thread'); invalMsgs(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const declineMut = useMutation({
    mutationFn: (id) => api.post(`/meetings/${id}/decline`, { reason: window.prompt('Reason (optional):') || null }),
    onSuccess: () => { invalMsgs(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const cancelMeetMut = useMutation({
    mutationFn: (id) => api.post(`/meetings/${id}/cancel`),
    onSuccess: () => { invalMsgs(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const transferMut = useMutation({
    mutationFn: () => api.post(`/chat/conversations/${active}/transfer`, { to_user_id: transferTo }),
    onSuccess: (r) => { toast.success(`Thread transferred to ${r.data.new_owner}`); setTransferOpen(false); setTransferTo(''); qc.invalidateQueries({ queryKey: ['chat-convs'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const renameMut = useMutation({
    mutationFn: (name) => api.patch(`/chat/conversations/${active}`, { name }),
    onSuccess: () => { toast.success('Thread renamed'); qc.invalidateQueries({ queryKey: ['chat-convs'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const archiveMut = useMutation({
    mutationFn: () => api.delete(`/chat/conversations/${active}`),
    onSuccess: () => { toast.success('Thread archived'); setActive(null); qc.invalidateQueries({ queryKey: ['chat-convs'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) return toast.error('Max attachment size is 15MB');
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await api.post('/chat/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPending((p) => [...p, r.data]);
      toast.success(`Attached ${r.data.name}`);
    } catch (err) { toast.error(errMsg(err)); }
  };

  const convLabel = (c) => c.type === 'group' || c.type === 'guardian' ? c.name : (c.member_names || []).find((n) => n !== (user?.name || user?.email)) || c.member_names?.[0];

  const receiptFor = (m) => {
    if (!activeConv || m.sender_id !== user?.id || m.kind) return null;
    const others = (activeConv.member_ids || []).filter((x) => x !== user.id);
    if (!others.length) return null;
    const ra = activeConv.read_at || {};
    const seen = others.filter((o) => (ra[o] || '') >= m.created_at);
    const names = seen.map((o) => (activeConv.member_names || [])[activeConv.member_ids.indexOf(o)]).filter(Boolean).map((n) => n.split(' ')[0]);
    return { seen: seen.length, total: others.length, names };
  };
  const lastOwnId = [...(messages || [])].reverse().find((m) => m.sender_id === user?.id && !m.kind)?.id;
  const canManage = activeConv && (activeConv.created_by === user?.id || activeConv.moderator_id === user?.id || ['global_admin', 'school_admin', 'principal'].includes(user?.role));

  return (
    <div data-testid="chat-page" className="h-[calc(100vh-8rem)] flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">{isStaff ? 'Internal · staff & guardian threads' : 'Your school conversations'}</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Chat</h1>
        </div>
        <Button data-testid="chat-new-button" onClick={() => setNewOpen(!newOpen)} className={`bg-slate-900 hover:bg-slate-800 ${isStaff ? '' : 'hidden'}`}><Plus size={16} className="mr-1" /> New chat</Button>
      </div>

      {newOpen && (
        <div className="bg-white border border-slate-200 rounded-md p-4 mb-4" data-testid="chat-new-panel">
          <div className="flex flex-wrap gap-2 mb-3">
            {(contacts || []).map((ct) => (
              <button key={ct.id} data-testid={`chat-contact-${ct.id}`}
                onClick={() => setSelected((s) => s.includes(ct.id) ? s.filter((x) => x !== ct.id) : [...s, ct.id])}
                className={`text-xs border rounded-full px-3 py-1.5 transition-colors ${selected.includes(ct.id) ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 hover:border-slate-900'}`}>
                {ct.name || ct.email} · {ct.role.replace(/_/g, ' ')}
              </button>
            ))}
            {(contacts || []).length === 0 && <p className="text-sm text-slate-400">No other staff in your scope.</p>}
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <Button data-testid="chat-start-dm" disabled={selected.length !== 1 || convMut.isPending}
              onClick={() => convMut.mutate({ type: 'dm', member_ids: selected })} variant="outline">Start direct message</Button>
            <Input data-testid="chat-group-name" className="w-48" placeholder="Group name" value={groupName} onChange={(e) => setGroupName(e.target.value)} />
            <Button data-testid="chat-start-group" disabled={selected.length < 2 || !groupName || convMut.isPending}
              onClick={() => convMut.mutate({ type: 'group', member_ids: selected, name: groupName })} variant="outline">
              <UsersThree size={16} className="mr-1" /> Create group ({selected.length})</Button>
          </div>
          {canGuardianThread && (
            <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2 flex-wrap items-center" data-testid="chat-guardian-thread-form">
              <p className="font-mono-label text-[10px] uppercase text-slate-400 w-full">Moderated guardian thread - quick updates about a student</p>
              <select data-testid="chat-gt-student-select" value={gtStudent} onChange={(e) => setGtStudent(e.target.value)}
                className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white w-64">
                <option value="">Select student...</option>
                {(students || []).filter((s) => !['exited', 'erased'].includes(s.status)).map((s) => <option key={s.id} value={s.id}>{`${s.name} - Grade ${s.grade}${s.section}`}</option>)}
              </select>
              <Button data-testid="chat-gt-start" variant="outline" disabled={!gtStudent || gtMut.isPending} onClick={() => gtMut.mutate()}>Start guardian thread</Button>
            </div>
          )}
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
        <div className="bg-white border border-slate-200 rounded-md overflow-y-auto" data-testid="chat-conversations">
          {(convs || []).map((c) => (
            <button key={c.id} data-testid={`chat-conv-${c.id}`} onClick={() => setActive(c.id)}
              className={`w-full text-left px-3 py-3 border-b border-slate-100 hover:bg-slate-50 transition-colors ${active === c.id ? 'bg-slate-100' : ''}`}>
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm truncate">{c.type === 'group' && <UsersThree size={14} className="inline mr-1" />}{convLabel(c)}</span>
                {c.type === 'guardian' && <span className="font-mono-label text-[8px] uppercase px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">moderated</span>}
                {c.unread > 0 && <span data-testid={`chat-unread-${c.id}`} className="ml-auto bg-slate-900 text-white text-[10px] rounded-full px-1.5 py-0.5 font-bold">{c.unread}</span>}
              </div>
              <p className="text-xs text-slate-400 truncate mt-0.5">{c.last_message_preview || 'No messages yet'}</p>
            </button>
          ))}
          {(convs || []).length === 0 && <p className="text-sm text-slate-400 p-4">No conversations yet - start one with "New chat".</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-md flex flex-col min-h-0">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-slate-400 text-sm" data-testid="chat-empty-state">Select or start a conversation</div>
          ) : (
            <>
              <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2 flex-wrap" data-testid="chat-thread-header">
                <span className="font-semibold text-sm">{activeConv ? convLabel(activeConv) : ''}</span>
                {activeConv?.type === 'group' && <span className="text-xs text-slate-400 font-normal">{activeConv.member_names.join(', ')}</span>}
                <div className="ml-auto flex items-center gap-1.5">
                  {isStaff && activeConv?.type === 'guardian' && (
                    <button data-testid="chat-share-statement" title="Drop latest fee statement into thread" disabled={stmtMut.isPending}
                      onClick={() => stmtMut.mutate()}
                      className="flex items-center gap-1 text-xs border border-orange-200 text-orange-700 rounded px-2 py-1 hover:bg-orange-50 transition-colors">
                      <FileText size={13} /> Statement
                    </button>
                  )}
                  <button data-testid="chat-meeting-request-button" title="Request a meeting" onClick={() => setMeetOpen(true)}
                    className="flex items-center gap-1 text-xs border border-slate-200 rounded px-2 py-1 hover:border-slate-900 transition-colors">
                    <CalendarPlus size={13} /> Meeting
                  </button>
                  {isStaff && activeConv?.type !== 'dm' && canManage && (
                    <>
                      <button data-testid="chat-transfer-button" title="Transfer thread to another staff member" onClick={() => setTransferOpen(true)}
                        className="flex items-center gap-1 text-xs border border-slate-200 rounded px-2 py-1 hover:border-slate-900 transition-colors">
                        <ArrowsLeftRight size={13} /> Transfer
                      </button>
                      <button data-testid="chat-rename-button" title="Rename thread"
                        onClick={() => { const n = window.prompt('New thread name:', activeConv?.name || ''); if (n) renameMut.mutate(n); }}
                        className="text-slate-400 hover:text-slate-900 p-1"><PencilSimple size={14} /></button>
                    </>
                  )}
                  {isStaff && canManage && (
                    <button data-testid="chat-archive-button" title="Archive thread"
                      onClick={() => { if (window.confirm('Archive this thread for everyone? It disappears from all chat lists.')) archiveMut.mutate(); }}
                      className="text-slate-400 hover:text-rose-600 p-1"><Archive size={14} /></button>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
                {(messages || []).map((m) => {
                  const own = m.sender_id === user?.id;
                  if (m.kind === 'system') {
                    return <p key={m.id} className="text-center text-[11px] text-slate-400 italic" data-testid={`chat-msg-${m.id}`}>{m.text}</p>;
                  }
                  const receipt = receiptFor(m);
                  const meeting = m.kind === 'meeting' ? meetingById[m.meeting_id] : null;
                  return (
                    <div key={m.id} className={`flex ${own ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${m.id}`}>
                      <div className={`group relative max-w-[75%] rounded-lg px-3 py-2 text-sm ${own ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-800'}`}>
                        {!own && <p className="text-[10px] font-bold opacity-70 mb-0.5">{m.sender_name}</p>}
                        {m.moderated ? (
                          <p className="italic opacity-60">[removed by moderator]</p>
                        ) : (
                          m.text && <p className="whitespace-pre-wrap break-words">{renderText(m.text)}</p>
                        )}
                        {meeting && (
                          <MeetingCard meeting={meeting} isStaff={isStaff} userId={user?.id} plugins={plugins}
                            accept={(x) => acceptMut.mutate(x)} decline={(id) => declineMut.mutate(id)} cancel={(id) => { if (window.confirm('Cancel this meeting?')) cancelMeetMut.mutate(id); }} />
                        )}
                        {isStaff && !m.moderated && !m.kind && (
                          <button data-testid={`chat-moderate-${m.id}`} title="Remove message (moderation)"
                            onClick={() => { if (window.confirm('Remove this message for everyone?')) modMut.mutate(m.id); }}
                            className="absolute -top-2 -right-2 hidden group-hover:flex w-5 h-5 items-center justify-center rounded-full bg-rose-600 text-white text-[10px] font-bold">×</button>
                        )}
                        {(m.attachments || []).map((a) => (
                          <div key={a.file_id} className="mt-1.5">
                            {a.content_type?.startsWith('image/') ? (
                              <a href={`${API}/chat/files/${a.file_id}`} target="_blank" rel="noreferrer">
                                <img src={`${API}/chat/files/${a.file_id}`} alt={a.name} className="max-h-48 rounded border border-slate-200" />
                              </a>
                            ) : (
                              <a href={`${API}/chat/files/${a.file_id}`} target="_blank" rel="noreferrer"
                                className={`flex items-center gap-1.5 text-xs underline ${own ? 'text-slate-200' : 'text-blue-600'}`}>
                                <Paperclip size={14} /> {a.name} ({Math.round(a.size / 1024)} KB)
                              </a>
                            )}
                          </div>
                        ))}
                        <p className="text-[9px] mt-1 text-slate-400 flex items-center gap-1 justify-end">
                          {m.created_at?.slice(11, 16)}
                          {receipt && (
                            <span data-testid={`msg-receipt-${m.id}`} title={receipt.seen ? `Seen by ${receipt.names.join(', ')}` : 'Delivered'}>
                              {receipt.seen === 0 ? <Check size={12} className="text-slate-400" /> :
                                <Checks size={13} className={receipt.seen === receipt.total ? 'text-orange-400' : 'text-slate-400'} weight="bold" />}
                            </span>
                          )}
                        </p>
                        {receipt && receipt.seen > 0 && m.id === lastOwnId && (activeConv?.type !== 'dm') && (
                          <p className="text-[9px] text-slate-400 text-right" data-testid={`msg-seenby-${m.id}`}>Seen by {receipt.names.join(', ')}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              {pending.length > 0 && (
                <div className="px-4 py-1.5 border-t border-slate-100 flex gap-2 flex-wrap" data-testid="chat-pending-attachments">
                  {pending.map((p) => (
                    <span key={p.file_id} className="text-xs bg-slate-100 rounded px-2 py-1 flex items-center gap-1">
                      <Paperclip size={12} /> {p.name}
                      <button onClick={() => setPending((x) => x.filter((y) => y.file_id !== p.file_id))} className="text-rose-500 font-bold ml-1">×</button>
                    </span>
                  ))}
                </div>
              )}
              {showEmoji && (
                <div className="px-4 py-2 border-t border-slate-100 flex gap-1 flex-wrap" data-testid="chat-emoji-picker">
                  {EMOJIS.map((e) => <button key={e} onClick={() => { setText((t) => t + e); setShowEmoji(false); }} className="text-xl hover:scale-125 transition-transform">{e}</button>)}
                </div>
              )}
              {showLinks && (
                <div className="px-4 py-2 border-t border-slate-100 flex gap-1.5 flex-wrap" data-testid="chat-module-links">
                  {MODULE_LINKS.map(([label, path]) => (
                    <button key={path} data-testid={`chat-link-${path.slice(1)}`} onClick={() => { setText((t) => `${t} ${path} `.trimStart()); setShowLinks(false); }}
                      className="text-xs border border-slate-200 rounded px-2 py-1 hover:border-slate-900">{label}</button>
                  ))}
                </div>
              )}
              <div className="p-3 border-t border-slate-100 flex gap-2 items-center">
                <button data-testid="chat-attach-button" onClick={() => fileRef.current?.click()} className="text-slate-500 hover:text-slate-900" title="Attach file"><Paperclip size={20} /></button>
                <button data-testid="chat-emoji-button" onClick={() => { setShowEmoji(!showEmoji); setShowLinks(false); }} className="text-slate-500 hover:text-slate-900" title="Emoji"><Smiley size={20} /></button>
                <button data-testid="chat-linkshare-button" onClick={() => { setShowLinks(!showLinks); setShowEmoji(false); }} className="text-slate-500 hover:text-slate-900" title="Share module link"><LinkSimple size={20} /></button>
                <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="chat-file-input" />
                <Input data-testid="chat-text-input" placeholder="Write a message..." value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && (text.trim() || pending.length)) { e.preventDefault(); sendMut.mutate(); } }} />
                <Button data-testid="chat-send-button" disabled={(!text.trim() && pending.length === 0) || sendMut.isPending}
                  onClick={() => sendMut.mutate()} className="bg-slate-900 hover:bg-slate-800 shrink-0"><PaperPlaneRight size={16} /></Button>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={meetOpen} onOpenChange={setMeetOpen}>
        <DialogContent data-testid="meeting-request-dialog">
          <DialogHeader><DialogTitle className="font-display">Request a meeting</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="meeting-purpose-input" placeholder="Purpose (e.g. discuss term progress)" value={meetForm.purpose}
              onChange={(e) => setMeetForm({ ...meetForm, purpose: e.target.value })} />
            <p className="font-mono-label text-[10px] uppercase text-slate-400">Propose up to 3 time slots</p>
            {meetForm.slots.map((s, i) => (
              <Input key={i} data-testid={`meeting-slot-input-${i}`} type="datetime-local" value={s}
                onChange={(e) => setMeetForm({ ...meetForm, slots: meetForm.slots.map((x, j) => j === i ? e.target.value : x) })} />
            ))}
            <Button data-testid="meeting-submit" className="w-full bg-orange-600 hover:bg-orange-500"
              disabled={!meetForm.slots.some(Boolean) || meetReqMut.isPending} onClick={() => meetReqMut.mutate()}>
              Send meeting request
            </Button>
            <p className="text-[10px] text-slate-400">A teacher confirms one slot and the video-call link (Meet / Teams / Zoom / Slack) is posted right here in the thread.</p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent data-testid="chat-transfer-dialog">
          <DialogHeader><DialogTitle className="font-display">Transfer thread</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Hand this thread's ownership (and moderation, for guardian threads) to another staff member.</p>
            <select data-testid="chat-transfer-select" value={transferTo} onChange={(e) => setTransferTo(e.target.value)}
              className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
              <option value="">Select staff member...</option>
              {(contacts || []).map((ct) => <option key={ct.id} value={ct.id}>{ct.name || ct.email} · {ct.role.replace(/_/g, ' ')}</option>)}
            </select>
            <Button data-testid="chat-transfer-confirm" className="w-full bg-slate-900 hover:bg-slate-800"
              disabled={!transferTo || transferMut.isPending} onClick={() => transferMut.mutate()}>Transfer</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
